import { useBillingPeriods } from 'api/BillingApi';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { sortObjectListByKey } from 'utils/arrayHelpers';
import {
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { localizeDateRange, localizeDateTime } from 'utils/l10n';
import { BillingPeriod } from '../types/dates';
import { PST_DATETIME_SUFFIX } from '../utils';
import { DateContext, defaultBillingPeriod } from './DateContext';

/** Reference to today for use in comparing selected period */
const today = format(new Date(), 'yyyy-MM-dd');

export const DateContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data, isError, isLoading } = useBillingPeriods({
    enabled: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const currentPeriod = data?.billingPeriods?.find(
    (period) => period.startDatePST <= today && period.endDatePST >= today,
  );

  const [startDate, setStartDate] = useSearchParamState({
    searchParamKey: 'startDatePST',
    stateValueWhenSearchParamIsUnset: currentPeriod?.startDatePST || '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });

  const [endDate, setEndDate] = useSearchParamState({
    searchParamKey: 'endDatePST',
    stateValueWhenSearchParamIsUnset: currentPeriod?.endDatePST || '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });

  const setDateRange = useCallback(
    (start: string, end: string) => {
      setEndDate(end);
      setStartDate(start);
    },
    [setStartDate, setEndDate],
  );

  /**
   * Billing Periods
   * Available billing periods for selection
   */

  const billingPeriods = useMemo((): BillingPeriod[] => {
    const sortedBillingPeriods = sortObjectListByKey(
      data?.billingPeriods || [],
      'startDatePST',
      true,
    );

    return sortedBillingPeriods.map((period) => {
      const startDateDate = new Date(period.startDatePST.concat(PST_DATETIME_SUFFIX));
      const endDateDate = new Date(period.endDatePST.concat(PST_DATETIME_SUFFIX));
      const rangeText = localizeDateRange(startDateDate, endDateDate).range || '';
      const endDateText = localizeDateTime(endDateDate);

      return {
        ...period,
        endDateText,
        isFutureDate: period.startDatePST > today,
        rangeText,
      };
    });
  }, [data?.billingPeriods]);

  const contextValue = useMemo(() => {
    const billingPeriod =
      billingPeriods.find((period) => period.startDatePST === startDate) || defaultBillingPeriod;

    return {
      billingCycleStart: billingPeriods[billingPeriods.length - 1]?.startDatePST,
      billingCycleEnd: billingPeriods[0]?.endDatePST,
      billingPeriod,
      billingPeriods,
      isError,
      isLoading,
      setDateRange,
    };
  }, [billingPeriods, isError, isLoading, setDateRange, startDate]);

  return <DateContext.Provider value={contextValue}>{children}</DateContext.Provider>;
};
